import { useEffect, useState } from "react";
import { axiosPostCamelToSnake } from "@root/shared/utils/axiosPostCamelToSnake";
import { showAlert } from "@root/shared/utils/showAlert";
import { navigateTo } from "@root/shared/utils/navigateTo";
import { type SearchResult } from "./type";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const newrelic: any;

type ReturnType = {
  isShowClientsDetail: {
    clientId: number;
    isShowShopName: boolean;
  }[];
  clientNameValue: string;
  shopNameValue: string;
  searchResults: SearchResult[];
  tooManyResultErrorText: string | undefined;
  noResultError: boolean;
  isSearching: boolean;
  handleClientSearch: () => Promise<void>;
  handleShowTextButton: (clientId: number) => void;
  handleChangeClientNameValue: (value: string) => void;
  handleChangeShopNameValue: (value: string) => void;
  handleReturnToMyPageTop: () => void;
};

type Response = {
  data: SearchResult[];
  message: string;
};

type ErrorResponse = {
  status: number;
  statusText?: string;
  data: {
    message: string;
  };
};

export const useSearchModal = (mypageTopPath: string, clientSearchPath: string): ReturnType => {
  const [isShowClientsDetail, setIsShowClientsDetail] = useState<
    { clientId: number; isShowShopName: boolean }[]
  >([]);
  const [clientNameValue, setClientNameValue] = useState("");
  const [shopNameValue, setShopNameValue] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [tooManyResultErrorText, setSearchResultErrorText] = useState<string | undefined>();
  const [noResultError, setNoResultError] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // 投稿先企業を検索するハンドラ
  const handleClientSearch = async () => {
    setIsSearching(true);
    const body = {
      formUserFrontendClientSearch: {
        clientName: clientNameValue,
        clientShopName: shopNameValue,
      },
    };
    setSearchResultErrorText(undefined);
    await axiosPostCamelToSnake
      .post<Response>(clientSearchPath, body)
      .then(({ data }) => {
        setIsSearching(false);
        setSearchResults(data.data);
        if (data.message) {
          setSearchResultErrorText(data.message);
          return;
        }
        if (data.data.length === 0) {
          setNoResultError(true);
        } else if (noResultError) {
          setNoResultError(false);
        }
      })
      .catch((error) => {
        const response: ErrorResponse = error.response as ErrorResponse;
        setIsSearching(false);
        setSearchResults([]);
        switch (response.status) {
          case 400:
            setSearchResultErrorText(response.data.message);
            break;
          default:
            if (typeof newrelic !== "undefined") {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call
              newrelic.noticeError(response.statusText);
            }
            showAlert("送信に失敗しました。時間をおいて再度お試しください。");
        }
      });
  };

  // 投稿先企業検索一覧の詳細表示
  const handleShowTextButton = (clientId: number) => {
    setIsShowClientsDetail((prevStates) =>
      prevStates.map((prevState) => ({
        clientId: prevState.clientId,
        isShowShopName: prevState.clientId === clientId ? true : prevState.isShowShopName,
      })),
    );
  };

  // 投稿先企業検索の入力情報
  const handleChangeClientNameValue = (value: string) => setClientNameValue(value);
  const handleChangeShopNameValue = (value: string) => setShopNameValue(value);

  // 中断して戻るハンドラ
  const handleReturnToMyPageTop = () => {
    navigateTo(mypageTopPath);
  };

  // 投稿先企業検索一覧の詳細表示 の状態更新
  useEffect(() => {
    setIsShowClientsDetail(
      searchResults.map((searchResult) => ({
        clientId: searchResult.clientId,
        isShowShopName: false,
      })),
    );
  }, [searchResults]);

  return {
    isShowClientsDetail,
    clientNameValue,
    shopNameValue,
    searchResults,
    tooManyResultErrorText,
    noResultError,
    isSearching,
    handleClientSearch,
    handleShowTextButton,
    handleChangeClientNameValue,
    handleChangeShopNameValue,
    handleReturnToMyPageTop,
  };
};
